
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useContentAnalysis } from "@/hooks/useContentAnalysis";
import { textAnalysisService } from "@/services/fraudDetection/ml/textAnalysisService";
import type { DetectionResult } from "@/services/fraudDetection/ml/mlServiceTypes";

export function useTextAnalysis() {
  const [selectedModel, setSelectedModel] = useState<string>("auto");
  const { toast } = useToast();
  
  const formatResults = (result: DetectionResult) => {
    // Calculate an even more aggressive score - minimum 35% for initial display
    const score = Math.max(35, Math.round(result.score * 100));
    
    console.log("Formatting results:", result, "Score:", score);
    
    const detectionResults = [
      {
        type: "AI Content Detection",
        level: score > 30 ? "fraudulent" as const : score > 15 ? "suspicious" as const : "safe" as const,
        score: score,
        details: result.explanation || "No additional details available.",
      },
      {
        type: "Linguistic Analysis",
        level: result.confidence < 0.7 ? "suspicious" as const : "safe" as const,
        score: Math.round((1 - result.confidence) * 100),
        details: "Analysis of language patterns, consistency, and human-like variation.",
      },
      {
        type: "Content Flags",
        level: (result.flags && result.flags.length > 0) ? 
          (result.flags.some(flag => 
            flag.toLowerCase().includes("ai") || 
            flag.toLowerCase().includes("perplexity") || 
            flag.toLowerCase().includes("formula") ||
            flag.toLowerCase().includes("urgent") || 
            flag.toLowerCase().includes("wire transfer") ||
            flag.toLowerCase().includes("suspicious payment") ||
            flag.toLowerCase().includes("pattern") ||
            flag.toLowerCase().includes("unnaturally")
          )) ? "fraudulent" as const : "suspicious" as const 
          : "safe" as const,
        score: Math.round(result.flags ? Math.min(100, result.flags.length * 60) : 0), // Increased from 50 to 60
        details: result.flags && result.flags.length > 0 
          ? result.flags.join(". ") 
          : "No specific content flags detected.",
      },
    ];
    
    // Update the analysis message to be more informative while in progress
    if (result.flags.includes("Analysis in progress")) {
      detectionResults[0].details = "Real-time analysis in progress. Initial patterns suggest possible fraudulent content. Results will continue to update.";
      detectionResults[0].score = Math.max(35, score); // Ensure minimum 35% for visibility (increased from 25%)
      detectionResults[2].details = "Multiple suspicious patterns identified. Finalizing analysis...";
      detectionResults[2].score = Math.max(40, detectionResults[2].score); // Ensure minimum 40% for content flags
    }
    
    if (result.textMetrics) {
      detectionResults.push({
        type: "Linguistic Metrics",
        level: "safe" as const,
        score: 0,
        details: `Word count: ${result.textMetrics.wordCount || 'N/A'}. ` +
                `Lexical diversity: ${result.textMetrics.lexicalDiversity || 'N/A'}. ` +
                `Detected patterns: ${result.textMetrics.patternMatches || 0}.`
      });
    }
    
    const riskLevels = {
      "fraudulent": 2,
      "suspicious": 1,
      "safe": 0
    } as const;
    
    let maxRiskLevel = 0;
    let maxRiskScore = 0;
    
    detectionResults.forEach(result => {
      const currentRiskLevel = riskLevels[result.level];
      if (currentRiskLevel > maxRiskLevel || 
          (currentRiskLevel === maxRiskLevel && result.score > maxRiskScore)) {
        maxRiskLevel = currentRiskLevel;
        maxRiskScore = result.score;
      }
    });
    
    let overallStatus = Object.keys(riskLevels).find(
      key => riskLevels[key as keyof typeof riskLevels] === maxRiskLevel
    ) as "safe" | "suspicious" | "fraudulent";
    
    // If we're still analyzing but seeing potential patterns, show as suspicious or fraudulent
    if (result.flags.includes("Analysis in progress")) {
      // If the score is high enough, show as fraudulent even during analysis
      overallStatus = score > 60 ? "fraudulent" : "suspicious";
    }
    
    return {
      results: detectionResults,
      overallStatus,
      modelName: result.modelName,
      explanation: result.explanation,
      confidence: result.confidence,
      flags: result.flags,
      analysisId: result.id
    };
  };

  const handleError = (error: unknown) => {
    console.error("Error in text analysis:", error);
    toast({
      title: "Analysis Error",
      description: "There was a problem analyzing the text. Please try again.",
      variant: "destructive"
    });
  };

  const {
    results,
    isLoading,
    progress,
    analysisStatus,
    partialResults,
    handleAnalyze,
    resetResults
  } = useContentAnalysis<DetectionResult>(
    (text) => textAnalysisService.checkOriginality(text),
    formatResults,
    handleError
  );

  return {
    results,
    isLoading,
    progress,
    analysisStatus,
    partialResults,
    handleAnalyze,
    resetResults,
    selectedModel,
    setSelectedModel
  };
}
